import { assertNever } from '../assert.js';
import { TENANT_KINDS } from '../tenancy/kind.js';
export const ROLES = [
    'platform-admin',
    'internal-ops',
    'deployment-engineer',
    'support-engineer',
    'sales-cs',
    'approver',
    'customer-admin',
    'customer-viewer',
];
/**
 * The roles a customer's own people hold (IM-153).
 *
 * Only these may be granted through a customer's user management, only on
 * `customer` tenants, and never with all-tenant scope. Everything else is a
 * staff role.
 */
export const CUSTOMER_ROLES = ['customer-admin', 'customer-viewer'];
export const isCustomerRole = (value) => CUSTOMER_ROLES.includes(value);
export const isRole = (value) => ROLES.includes(value);
/** Read-only across the board — the floor every role except approver builds on. */
const READ_ONLY = ['tenant:read', 'account:read', 'deployment:read'];
const DIAGNOSTICS = ['logs:read', 'diagnostics:read'];
/**
 * Causing a deployment's outputs to be regenerated.
 *
 * Separate from `deployment:read` on purpose, and the reason is the same one
 * that separates `logs:read`: reading a deployment's stored endpoints and
 * starting a container in the customer's account to produce new ones are
 * different acts, and only the second reaches their infrastructure. Because
 * `deployment:read` is the floor every role builds on, authorizing the run with
 * it gave `sales-cs` — "status only" — a control that dispatches into a customer
 * account. That was unreachable only because the console had no button for it
 * (IM-74), which is why IM-75 is fixed in the same change.
 *
 * `support-engineer` holds this. They already read logs and build diagnostic
 * bundles, an `output` run changes no infrastructure, and they are the role most
 * likely to need current endpoints. `sales-cs` and `approver` do not.
 */
const REFRESH_OUTPUTS = ['outputs:refresh'];
/**
 * What `deployment-engineer` holds, named so `internal-ops` can be defined as
 * it plus one capability rather than as a second list.
 *
 * Two lists that are meant to differ by one item is how they come to differ by
 * three: a capability added to the engineer's bundle would silently not reach
 * the ops role, and nothing would say so. This makes "exactly one more" a
 * property of the code rather than of a test — the test then guards the
 * intent rather than restating the implementation.
 */
const DEPLOYMENT_ENGINEER = [
    ...READ_ONLY,
    ...DIAGNOSTICS,
    /*
     * Cost, since IM-130. Held by whoever operates deployments — which is the
     * population the question "what is this costing" is actually asked by — and
     * therefore by `internal-ops` too, whose whole job is watching Qrvey's own
     * spend. Deliberately not by `sales-cs`, whose remit is status.
     */
    'cost:read',
    'account:onboard',
    'grant:request',
    'deployment:create',
    'deployment:upgrade',
    'plan:run',
    ...REFRESH_OUTPUTS,
    'diagnostics:bundle',
    /*
     * Held here even though `deployment:destroy` is not, and the difference is
     * the reason given there: destroy is withheld because it is the one
     * irreversible act. Hiding is reversible by construction — the record stays,
     * the restore route puts it back, and the guards refuse while anything is
     * live — so the argument for withholding it does not apply.
     */
    'records:manage',
];
/**
 * Roles as capability bundles.
 *
 * Deliberately a total switch rather than a lookup object: adding a role becomes
 * a compile error here instead of silently granting nothing, which would look
 * exactly like a permissions bug in production.
 */
export const capabilitiesFor = (role) => {
    switch (role) {
        case 'platform-admin':
            // Everything, including granting access to others.
            return [
                ...READ_ONLY,
                ...DIAGNOSTICS,
                'tenant:manage',
                'account:onboard',
                'grant:request',
                'deployment:create',
                'deployment:upgrade',
                'deployment:destroy',
                'deployment:approve',
                'plan:run',
                ...REFRESH_OUTPUTS,
                'diagnostics:bundle',
                'audit:read',
                'version:manage',
                'access:manage',
                'records:manage',
                'account:settings',
                'cost:read',
                'budget:manage',
                // Invites a customer's first admin (IM-153) — the one invitation a
                // customer cannot send themselves.
                'users:manage',
            ];
        case 'internal-ops':
            /*
             * Qrvey's own operations: `deployment-engineer` plus destroy (IM-137).
             *
             * Destroy is withheld from `deployment-engineer` because it is the one
             * irreversible act, and that is right for a role that changes a
             * *customer's* infrastructure. It is backwards for the team whose routine
             * job is tearing down Qrvey's own temporary instances: under the older
             * roles they would create instances all week and ask a platform-admin to
             * remove each one, which makes the most common action the most obstructed
             * — and an obstruction on cleanup is how instances stay up.
             *
             * **Not platform-admin**, which would work today and hand them three
             * things the job does not need: `access:manage` (grant anyone any role,
             * including their own), `tenant:manage`, and `account:settings` — the
             * switch that blocks new instances, which exists partly as a check on the
             * people creating them. It would also make the audit trail less useful:
             * every decision records the role held at the time, so if everyone is
             * platform-admin the record stops distinguishing who was doing what.
             *
             * **Confined to Qrvey's own tenants by `tenantKindsFor`**, since IM-150.
             * Capability and scope are independent dimensions, so until then
             * `internal-ops` scoped to a customer's tenant could destroy that
             * customer's production instance, and the only thing preventing it was
             * whoever assigned the role. The kind is now a third dimension, checked
             * in `authorize`.
             */
            return [...DEPLOYMENT_ENGINEER, 'deployment:destroy'];
        case 'deployment-engineer':
            // Can change infrastructure but not destroy it, and cannot alter anyone's
            // access. Destroy is deliberately withheld: it is the one irreversible act.
            return DEPLOYMENT_ENGINEER;
        case 'support-engineer':
            // Diagnose anything, change no infrastructure. Refreshing outputs runs
            // the installer read-only, which is diagnosis, not change.
            return [...READ_ONLY, ...DIAGNOSTICS, ...REFRESH_OUTPUTS, 'diagnostics:bundle'];
        case 'sales-cs':
            // Status only. No logs or diagnostics — those carry operational detail
            // that a commercial conversation does not need.
            return [...READ_ONLY];
        case 'approver':
            // Held alongside another role. On its own it can only approve.
            return [...READ_ONLY, 'deployment:approve'];
        case 'customer-admin':
            /*
             * A customer running their own estate (IM-153): onboard their accounts,
             * deploy, upgrade and destroy their own instances, read what they cost,
             * and decide who else on their side may do so.
             *
             * `users:manage` rather than `access:manage`: the latter is the staff
             * screen that assigns anyone to any customer, and it is staff-only by
             * route. This one is tenant-scoped, so it reaches only their own people,
             * and `validateUserChange` keeps it to the two customer roles.
             *
             * No `records:manage` yet — hiding records was built for Qrvey's own
             * housekeeping, and a customer hiding their own instances is a decision
             * worth making on purpose. No `logs:read` either; see the viewer.
             */
            return [
                ...READ_ONLY,
                'account:onboard',
                'deployment:create',
                'deployment:upgrade',
                'deployment:destroy',
                'plan:run',
                ...REFRESH_OUTPUTS,
                'cost:read',
                'users:manage',
            ];
        case 'customer-viewer':
            /*
             * Status, deployments and cost, and nothing that acts. `logs:read` is left
             * out on purpose for both customer roles for now: the logs are redacted in
             * the customer's own account and are theirs, but "can see status" and "can
             * read logs" are separate for `sales-cs` too, and granting it should be a
             * decision rather than a default.
             */
            return [...READ_ONLY, 'cost:read'];
        default:
            return assertNever(role, 'capabilitiesFor');
    }
};
export const roleHasCapability = (role, capability) => capabilitiesFor(role).includes(capability);
/**
 * Which kinds of tenant a role may act on (IM-150).
 *
 * The third dimension beside capability and scope. Capability says *what*,
 * scope says *which customers*, and this says *whose* — a customer's, or
 * Qrvey's own. Before it, confining `internal-ops` to Qrvey's tenants was
 * something whoever assigned the role had to remember, and an assignment
 * scoped to a customer handed their production instance to a role whose whole
 * job is tearing instances down.
 *
 * A total switch, like `capabilitiesFor`: a new role does not compile until it
 * says whose tenants it may touch. The customer roles (IM-153) will be
 * `['customer']`, and the mirror image of this rule is what keeps them off
 * Qrvey's.
 */
export const tenantKindsFor = (role) => {
    switch (role) {
        case 'internal-ops':
            return ['qrvey'];
        case 'customer-admin':
        case 'customer-viewer':
            // The mirror image of internal-ops: a customer's people act on customer
            // tenants only, so no assignment can point one at Qrvey's own.
            return ['customer'];
        case 'platform-admin':
        case 'deployment-engineer':
        case 'support-engineer':
        case 'sales-cs':
        case 'approver':
            // Staff who work on customers and on Qrvey's own alike.
            return TENANT_KINDS;
        default:
            return assertNever(role, 'tenantKindsFor');
    }
};
/** Whether a role is confined to some kinds — which is when a tenant's kind has to be read. */
export const isKindRestricted = (role) => tenantKindsFor(role).length < TENANT_KINDS.length;
//# sourceMappingURL=roles.js.map